/**
 * CustomerCard Component - Mobile-first card for displaying customers
 * Displays customer information in a relationship-focused card format
 */

import React from "react";
import {
  Card,
  Typography,
  Button,
  Box,
  Chip,
} from "@mui/material";
import { Visibility } from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { useDeviceType } from "../../leads/hooks/useDeviceType";
import {
  formatPhoneDisplay,
  formatLastActive,
} from "../utils/customerUtils";

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: 12,
  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
  transition: "all 0.2s ease",
  cursor: "pointer",
  border: "1px solid transparent",
  "&:hover": {
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.12)",
    transform: "translateY(-1px)",
    borderColor: "rgba(0, 0, 0, 0.12)",
  },
  "&:active": {
    transform: "scale(0.98)",
  },
}));

const CardContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const NameText = styled(Typography)({
  fontWeight: 600,
  fontSize: "1.125rem",
  marginBottom: 4,
});

const PhoneText = styled(Typography)({
  fontSize: "0.875rem",
  color: "rgba(0, 0, 0, 0.6)",
  marginBottom: 12,
});

const ActivityRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 8,
  marginBottom: 12,
  flexWrap: "wrap",
});

const ActivityText = styled(Typography)({
  fontSize: "0.875rem",
  color: "rgba(0, 0, 0, 0.6)",
});

const StatusChip = styled(Chip)(({ status }) => ({
  fontWeight: 600,
  fontSize: "0.75rem",
  height: 24,
  backgroundColor:
    status === "active"
      ? "rgba(76, 175, 80, 0.1)"
      : "rgba(158, 158, 158, 0.1)",
  color:
    status === "active"
      ? "rgba(76, 175, 80, 0.9)"
      : "rgba(158, 158, 158, 0.9)",
}));

/**
 * CustomerCard Component
 * @param {Object} props
 * @param {Object} props.customer - Customer object
 * @param {Function} props.onView - Callback when card is clicked
 * @param {boolean} [props.dense] - Dense layout for tablet
 */
const CustomerCard = ({ customer, onView, dense = false }) => {
  const { isTablet } = useDeviceType();

  if (!customer) return null;

  const handleCardClick = () => {
    if (onView) {
      onView(customer);
    }
  };

  const phone = formatPhoneDisplay(customer.phone);
  const leadCount = customer.leadIds?.length || 0;
  const quoteCount = customer.quoteIds?.length || 0;
  const invoiceCount = customer.invoiceIds?.length || 0;
  const totalActivity = leadCount + quoteCount;
  const lastActive = customer.lastActivityDate || customer.updatedAt;
  const isActive = customer.status === "active";

  return (
    <StyledCard onClick={handleCardClick}>
      <CardContent>
        {/* Name + Phone */}
        <Box mb={1.5}>
          <NameText variant="body1" component="div">
            {customer.name || "Unnamed Customer"}
          </NameText>
          {phone && (
            <PhoneText variant="body2">{phone}</PhoneText>
          )}
        </Box>

        {/* Activity Summary */}
        <ActivityRow>
          {totalActivity > 0 && (
            <ActivityText variant="body2">
              {totalActivity} Activity{totalActivity !== 1 ? "s" : ""} · {invoiceCount} Invoice
              {invoiceCount !== 1 ? "s" : ""}
            </ActivityText>
          )}
          {totalActivity === 0 && invoiceCount === 0 && (
            <ActivityText variant="body2" sx={{ fontStyle: "italic" }}>
              No activity yet
            </ActivityText>
          )}
        </ActivityRow>

        {/* Status + Last Active */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={1.5}
        >
          <StatusChip
            label={isActive ? "Active" : "Dormant"}
            status={isActive ? "active" : "dormant"}
            size="small"
          />
          <ActivityText variant="caption">
            Last active: {formatLastActive(lastActive)}
          </ActivityText>
        </Box>

        {/* Action Button */}
        <Button
          variant="outlined"
          startIcon={<Visibility />}
          fullWidth
          onClick={(e) => {
            e.stopPropagation();
            handleCardClick();
          }}
          sx={{
            mt: 1,
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          View Customer
        </Button>
      </CardContent>
    </StyledCard>
  );
};

export default CustomerCard;

